// @ts-strict-ignore
import { ProjectGroupService } from '@project/service/project-group.service';
import { WagoApiStatusCode } from '@shared/service/wago-api-status-code';
import { FilterItem, ProjectNavigationModel, ProjectNavigationTab, UserIdName } from '../project-navigation.model';
import { Injectable } from '@angular/core';
import { OperationType } from './operation-type';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { Log } from '@shared/debug/log';
import { UserModel } from '@user/model/user.model';
import { ArticleProjectGroupLoadingService } from '@project/components/article/service/article-project-group-loading.service';
import { ProjectGroupLoadingService } from '@project/components/project/service/project-group-loading.service';
import { ProjectGroupUtils } from '@project/components/project/util/project-group-utils';
import { ProjectHeadVO, ProjectVO, UserInfoVO } from '@project/model/vo/project.vo';
import { lastValueFrom, Observable, of, Subject, Subscription } from 'rxjs';
import { WagoApiClient } from '@shared/service/wago-api.client';
import { map, switchMap, tap } from 'rxjs/operators';
import { SharedObjectInfoDataService } from '@project/components/project/service/shared-object-info-data.service';
import { UserVO } from '@user/model/vo/user.vo';
import { UserSettings } from '@user/model/user-settings';
import { ProjectModel } from '@project/model/project.model';
import { SystemConfigurationService } from '@shared/system-configuration/system-configuration-loading.service';
import { ArrayUtils } from '@shared/util/array-utils';
import { StringUtils } from '@shared/util/string-utils';
import { ProjectStatesDef } from '@project/components/project/constants/project-states-def';
import { ProjectGroupDataService } from '@project/components/import-export/service/project-group-data.service';
import { AlertButton } from '@shared/alert/alertButton';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ProjectService } from '@project/components/import-export/service/project.service';
import { CloneUtils } from '@shared/util/clone-utils';
import { Dictionary } from 'typescript-collections';
import { ProjectSettingsNotification, ProjectSettingsService } from '@project/components/project/project-settings/project-settings.service';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleProjectGroupDataService } from '@project/components/article/manager/article-project-group-data.service';
import { CopyProjectOperationProperties } from '@project/components/project/copy/copy.component';
import { ProjectDataService } from '@project/service/project-data.service';
import { ProjectNavigationPresenter } from '../project-navigation.presenter';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { ProjectComponentPresenter } from '@project/components/project-component.presenter';
import { HttpStatusCode } from '@angular/common/http';

@Injectable({ providedIn: "root" })
export class ProjectNavigationService {
  private projectGroupUpdatedSubscription: Subscription;

  public _filterBySearch = new Subject<void>();
  public _compositeCheckBoxChanged = new Subject<void>();

  public constructor(
    private userModel: UserModel,
    private userSettings: UserSettings,
    private projectModel: ProjectModel,
    private articleProjectGroupLoadingService: ArticleProjectGroupLoadingService,
    private projectGroupLoadingService: ProjectGroupLoadingService,
    private sharedObjectInfoDataService: SharedObjectInfoDataService,
    private systemConfigurationService: SystemConfigurationService,
    private projectGroupDataService: ProjectGroupDataService,
    private alertHelper: AlertHelper,
    private projectService: ProjectService,
    private projectDataService: ProjectDataService,
    private projectSettingsService: ProjectSettingsService,
    private articleProjectGroupDataService: ArticleProjectGroupDataService,
    private projectComponentPresenter: ProjectComponentPresenter,
    private projectNavigationPresenter: ProjectNavigationPresenter,
    private projectGroupService: ProjectGroupService,
  ) {}

  public async initializeProjectNavigationData(model: ProjectNavigationModel): Promise<void> {
    model.rootProjectGroup ??= this.createRootProjectGroup();
    await this.setSpecialAndAssignedGroups(model);
    await this.updateSharedFilter(model);
  }

  public get filterBySearch(): Observable<void> {
    return this._filterBySearch.asObservable();
  }

  public get compositeCheckBoxChanged(): Observable<void> {
    return this._compositeCheckBoxChanged.asObservable();
  }

  public triggerFilterBySearch(): void {
    this._filterBySearch.next();
  }

  public triggerCompositeCheckBoxChanged(): void {
    this._compositeCheckBoxChanged.next();
  }

  public async loadCurrentProjectGroup(model: ProjectNavigationModel): Promise<void> {
    model.rootProjectGroup = this.createRootProjectGroup();
    const projectGroup = await this.projectGroupLoadingService.loadProjectGroupById(this.projectModel.currentProject.projectGroup.id, 1);
    model.setCurrentViewedProjectGroup(projectGroup);
  }

  public async openPreselectedProjectGroupInMyOwnTab(model: ProjectNavigationModel) {
    if (model.compositeCheckBoxValue) {
      return this.openPreselectedProjectGroup(model);
    }
    model.searchMode = false;
    await this.updateSharedFilter(model);
    await this.setSpecialAndAssignedGroups(model);

    try {
      const preselectedGroupId: number = this.getCurrentProjectGroupId(model);
      const projectGroups = await this.projectGroupLoadingService.loadPreselectedProjectGroups(
        preselectedGroupId,
        0,
        model.currentModel.importType,
      );
      const isOwnUserGroups = projectGroups.every((group) => group.groupOwner.userId === this.userModel.user.id);
      if (!isOwnUserGroups) {
        model.clearCurrentViewedProjectGroup();
        model.currentProjectFolder = null;
        await this.preselectedGroupsSuccessHandler(model, []);
      } else {
        await this.preselectedGroupsSuccessHandler(model, projectGroups);
      }
    } catch (error) {
      this.preselectedGroupsFailureHandler(model, error);
    }
  }

  public async openPreselectedProjectGroup(model: ProjectNavigationModel): Promise<void> {
    model.rootProjectGroup = this.createRootProjectGroup();

    if (model.compositeCheckBoxValue) {
      try {
        const projectGroups = await this.articleProjectGroupLoadingService.loadBaseArticleProjectGroups();
        await this.preselectedGroupsSuccessHandler(model, projectGroups);
      } catch (error) {
        this.preselectedGroupsFailureHandler(model, error);
      }
    } else {
      const preselectedGroupId: number = this.getCurrentProjectGroupId(model);
      await this.updateSharedFilter(model);
      await this.setSpecialAndAssignedGroups(model);

      try {
        const projectGroups = await this.projectGroupLoadingService.loadPreselectedProjectGroups(
          preselectedGroupId,
          0,
          model.currentModel.importType,
        );
        await this.preselectedGroupsSuccessHandler(model, projectGroups);
      } catch (error) {
        this.preselectedGroupsFailureHandler(model, error);
      }
    }
  }

  private async setSpecialAndAssignedGroups(model: ProjectNavigationModel): Promise<void> {
    const projectGroups = await this.projectGroupLoadingService.loadDefaultImportGroups();
    model.setSpecialGroups(projectGroups);

    const myAssignedProjectGroup = await this.projectGroupLoadingService.loadMyAssignedProjectGroup(
      this.userModel.user.id,
      2,
      model.currentModel.importType,
    );
    this.setMyAssignedProjectGroup(model, myAssignedProjectGroup, this.userModel.user.id);
  }

  private async updateSharedFilter(model: ProjectNavigationModel): Promise<void> {
    model.sharedFilter = ``;

    try {
      const users = await lastValueFrom(
        this.sharedObjectInfoDataService.getSharedUsers(ProjectNavigationModel.MAX_SEEN_SHARED_USERS_AMOUNT, ""),
      );
      model.updateSharedFilterEnabled(users, this.userModel.user);
    } catch (error) {
      Log.error("Could not load sharedUsers", error);
    }
  }

  private async preselectedGroupsSuccessHandler(model: ProjectNavigationModel, projectGroups: ProjectGroupVO[]) {
    model.isInit = true;
    const groups = this.excludeDefaultUserGroups(projectGroups);


    if (this.isPreselectionAvailable(model, groups)) {
      await this.openPreselectedGroup(model, groups);
    } else {
      if (model.currentProjectFolder?.groupOwner?.userId !== this.userModel.user.id) {
        model.currentProjectFolder = null;
        model.clearCurrentViewedProjectGroup();
      }
      this.openOwnUser(model);
    }
  }

  private preselectedGroupsFailureHandler(model: ProjectNavigationModel, error) {
    if (!WagoApiClient.isAccessDeniedError(error)) {
      this.openOwnUser(model);
    }
  }

  public shareProjectLink(project: ProjectHeadVO): void {
    this.projectComponentPresenter.openShareProjectLinkPopup(
      project.id,
      project.currentVersion,
      project.modificationDateServerTimeStamp,
      project.articleNumberLong.reducedArticleNumberLong,
      project.name,
    );
  }

  public setMyAssignedProjectGroup(model: ProjectNavigationModel, myAssignedProjectGroup: ProjectGroupVO, userId: number): void {
    if (!myAssignedProjectGroup) {
      return;
    }
    const rootGroup = new ProjectGroupVO();
    rootGroup.id = myAssignedProjectGroup.id;
    rootGroup.groupName = ProjectNavigationModel.ROOT_NAME_MY_ASSIGNED;
    rootGroup.groupOwner = new UserInfoVO(userId);
    rootGroup.groups = myAssignedProjectGroup.groups;
    rootGroup.projects = [];
    model.rootProjectGroup.groups.push(rootGroup);
    model.isMyAssignedProjectsPresented = true;
  }

  private async openPreselectedGroup(model: ProjectNavigationModel, preselectedProjectGroups: ProjectGroupVO[]): Promise<void> {
    this.addChainToModel(model, preselectedProjectGroups);
    await this.selectPreselectedChainToModel(model, preselectedProjectGroups);
    if (model.canNavigateIntoProjectGroups && !model.compositeCheckBoxValue) {
      model.setCurrentViewedProjectGroup(preselectedProjectGroups[preselectedProjectGroups.length - 1]);
    }
  }

  private async selectPreselectedChainToModel(model: ProjectNavigationModel, preselectedProjectGroups: ProjectGroupVO[]) {
    let selectedGroup: ProjectGroupVO = model.getRootProjectGroupToPreselect(preselectedProjectGroups, this.userModel.user.id);
    if (!selectedGroup && preselectedProjectGroups.length > 0) {
      selectedGroup = preselectedProjectGroups[0];
    }
    if (model.compositeCheckBoxValue) {
      this.openProjectGroup(model, selectedGroup);
      return;
    } else {
      for (const group of preselectedProjectGroups) {
        const foundGroup = selectedGroup.groups.find(groupOfSelectedGroup => group.id === groupOfSelectedGroup.id);
        if (foundGroup) {
          selectedGroup = foundGroup;
        }
      }

      if (selectedGroup.groupType === ProjectGroupUtils.PROJECT) {
        const parentGroup = model.parentGroupMap.getValue(selectedGroup.id);

        await this.openGroup(model, parentGroup);
        this.openProjectGroup(model, selectedGroup);
        this.preselectProject(model, selectedGroup);
      } else {
        if (model.currentModel.isMoveOrCopy) {
          selectedGroup = model.parentGroupMap.getValue(selectedGroup.id);
        }
        this.openGroup(model, selectedGroup);
      }
    }
  }

  public openGroup(model: ProjectNavigationModel, selectedProjectGroup: ProjectGroupVO): Promise<void> {
    if (selectedProjectGroup.id === ProjectNavigationModel.ROOT_USER_ID) {
      if (selectedProjectGroup.groupOwner.userId === this.userModel.user.id) {
        return this.openOwnUser(model, selectedProjectGroup);
      } else if (ProjectGroupUtils.isDefaultImportProjectGroupType(selectedProjectGroup.groupType)) {
        return this.openSpecialProjectGroup(model, selectedProjectGroup);
      } else {
        return this.openSharedUser(model, new UserIdName(selectedProjectGroup.groupOwner.userId, selectedProjectGroup.groupOwner.username));
      }
    } else if (selectedProjectGroup.groupName === ProjectNavigationModel.ROOT_NAME_MY_ASSIGNED) {
      return this.openMyAssignedProjects(model);
    } else if (model.currentFilterItem && !(model.currentFilterItem instanceof UserIdName)) {
      return this.openSpecialProjectGroup(model, selectedProjectGroup);
    } else {
      return this.openProjectGroup(model, selectedProjectGroup);
    }
  }

  public async openSpecialProjectGroup(model: ProjectNavigationModel, selectedProjectGroup: ProjectGroupVO): Promise<void> {
    let currentFilterItem: ProjectGroupVO = selectedProjectGroup;
    while (currentFilterItem.parentGroup) {
      currentFilterItem = currentFilterItem.parentGroup;
    }

    this.updateNavigationChain(
      model,
      this.buildNavigationChain(model, selectedProjectGroup),
      currentFilterItem,
      ProjectNavigationTab.Shared,
    );

    model.clearCurrentViewedProjectGroup();
    if (selectedProjectGroup.childContentLoaded) {
      model.currentProjectFolder = selectedProjectGroup;
      return Promise.resolve();
    }

    const loadedProjectGroup = await this.projectGroupLoadingService.loadProjectGroupById(
      selectedProjectGroup.id,
      1,
      null,
      model.currentModel.importType,
    );

    try {
      this.addLoadedProjectGroupToData(model, selectedProjectGroup, loadedProjectGroup);
      model.currentProjectFolder = selectedProjectGroup;
    } catch (ex) {
      Log.info(ex);
    }
    return Promise.resolve();
  }

  private addChainToModel(model: ProjectNavigationModel, preselectedProjectGroups: ProjectGroupVO[]) {
    if (preselectedProjectGroups[0].id === ProjectNavigationModel.ROOT_USER_ID) {
      preselectedProjectGroups.shift();
    }

    const isMyAssignedProjectsGroup: boolean =
      ProjectGroupUtils.isMyAssignedProjectsGroup(preselectedProjectGroups[0]) &&
      preselectedProjectGroups[0].groupOwner.userId === this.userModel.user.id;
    const groupNameToFind = isMyAssignedProjectsGroup
      ? ProjectNavigationModel.ROOT_NAME_MY_ASSIGNED
      : ProjectNavigationModel.ROOT_NAME_MY_PROJECTS;
    let base = model.rootProjectGroup.groups.find((group) => group.groupName === groupNameToFind);

    if (!base) {
      base = this.createNewBaseProjectGroup(preselectedProjectGroups);
      model.rootProjectGroup.groups.push(base);
    }

    if (model.compositeCheckBoxValue) {
      base.groups = preselectedProjectGroups;
      base.groups.forEach((g) => model.parentGroupMap.setValue(g.id, base));
      base.childContentLoaded = true;
    } else {
      if (isMyAssignedProjectsGroup) {
        base.groups = this.addGroupsToList(base.groups, preselectedProjectGroups[0].groups);
        preselectedProjectGroups.shift();
      } else {
        base.groups = this.addGroupsToList(base.groups, [preselectedProjectGroups[0]]);
      }
      base.groups.forEach((g) => model.parentGroupMap.setValue(g.id, base));

      let loopBaseGroup = base;
      for (const preselectedProjectGroup of preselectedProjectGroups) {
        const currentProjectGroup = loopBaseGroup.groups.find((projectGroup) => projectGroup.id === preselectedProjectGroup.id);
        if (!currentProjectGroup) continue;
        currentProjectGroup.groups = this.addGroupsToList(currentProjectGroup.groups, preselectedProjectGroup.groups);
        currentProjectGroup.groups.forEach((g) => model.parentGroupMap.setValue(g.id, currentProjectGroup));
        currentProjectGroup.projects = preselectedProjectGroup.projects;
        currentProjectGroup.childContentLoaded = currentProjectGroup.childContentLoaded || preselectedProjectGroup.childContentLoaded;
        loopBaseGroup = currentProjectGroup;
      }
    }
  }

  private createNewBaseProjectGroup(preselectedProjectGroups: ProjectGroupVO[]) {
    const base = new ProjectGroupVO();
    base.id = ProjectNavigationModel.ROOT_USER_ID;
    base.groupName =
      preselectedProjectGroups[0].groupOwner.userId === this.userModel.user.id
        ? ProjectNavigationModel.ROOT_NAME_MY_PROJECTS
        : ProjectNavigationModel.ROOT_NAME_SHARED;
    base.groupOwner = new UserInfoVO(preselectedProjectGroups[0].groupOwner.userId, preselectedProjectGroups[0].groupOwner.username);
    base.projects = [];
    return base;
  }

  private addGroupsToList(baseProjectGroups: ProjectGroupVO[], newProjectGroups: ProjectGroupVO[]): ProjectGroupVO[] {
    if (!baseProjectGroups) {
      baseProjectGroups = [];
    }
    newProjectGroups.forEach((g) => {
      if (!baseProjectGroups.find((bg) => bg.id === g.id)) {
        baseProjectGroups.push(g);
      }
    });
    return baseProjectGroups;
  }

  private preselectProject(model: ProjectNavigationModel, selectedGroup: ProjectGroupVO): void {
    let preselectedItem: ProjectHeadVO | ProjectGroupVO;
    if (model.currentModel.preselectGroup) {
      preselectedItem = selectedGroup;
    } else {
      const isValidProjectOpened = NumberUtils.isNumber(this.projectModel.currentProject?.id);
      const lastSelectedProjectId = isValidProjectOpened ? this.projectModel.currentProject?.id : this.userSettings.lastSelectedProjectId;

      preselectedItem = selectedGroup.projects.find((p) => p.id === lastSelectedProjectId);
      preselectedItem = preselectedItem ? preselectedItem : selectedGroup;
    }
    if (preselectedItem) {
      model.preSelectItem(preselectedItem);
      model.scrollToSelected.next();
    }
  }

  public get isSaveLoadCompositeArticlesAvailable(): boolean {
    return !!(
      this.systemConfigurationService.currentConfiguration &&
      this.systemConfigurationService.currentConfiguration.adminToolsEnabled &&
      this.userModel.user.hasPermission("SAVE_COMPOSITE_ARTICLES") &&
      this.userModel.user.hasPermission("MAINTAIN_ARTICLES")
    );
  }

  private isPreselectionAvailable(model: ProjectNavigationModel, groupsToPreselect: ProjectGroupVO[]): boolean {
    if (ArrayUtils.isEmpty(groupsToPreselect) || model.currentViewedProjectGroup != null) {
      return false;
    }
    // check preselection for shared project group
    if (groupsToPreselect[0].groupOwner.userId !== this.userModel.user.id && !model.currentModel.visibleSharedProjects) {
      return false;
    }
    // check preselection in CAE mode
    if (model.currentModel.importType && !groupsToPreselect.find((i) => i.importType === ProjectGroupVO.CAE_IMPORT_TYPE)) {
      return false;
    }
    // check preselection for my assigned projects group
    if (ProjectGroupUtils.isMyAssignedProjectsGroup(groupsToPreselect[0]) && !model.isMyAssignedProjectsPresented) {
      return false;
    }
    return true;
  }

  private getCurrentProjectGroupId(model) {
    if (model.compositeCheckBoxValue) {
      return this.userSettings.lastSelectedArticleProjectGroupId;
    }
    if (
      this.projectModel.currentProject &&
      this.projectModel.currentProject.projectGroup &&
      this.projectModel.currentProject.projectGroup.importType === ProjectGroupVO.CAE_IMPORT_TYPE
    ) {
      return this.projectModel.currentProject.projectGroup.id;
    } else if (!isNaN(model.currentModel.lastSelectedGroupId)) {
      return model.currentModel.lastSelectedGroupId;
    } else {
      return this.userSettings.lastSelectedProjectGroupId;
    }
  }

  private createRootProjectGroup(projectGroups: ProjectGroupVO[] = [], projects: ProjectVO[] = []): ProjectGroupVO {
    const projectGroup = new ProjectGroupVO();
    projectGroup.id = ProjectNavigationModel.ROOT_ID;
    projectGroup.groupName = ProjectNavigationModel.ROOT_NAME;
    projectGroup.groups = projectGroups;
    projectGroup.projects = projects;
    return projectGroup;
  }

  private excludeDefaultUserGroups(groups: ProjectGroupVO[]): ProjectGroupVO[] {
    return groups.filter((g) => g.groupOwner.userId !== UserVO.DEFAULT_GUEST_ACCOUNT_ID);
  }

  public updateNavigationChain(
    model: ProjectNavigationModel,
    navigationChain: ProjectGroupVO[],
    filterItem: FilterItem,
    tab: ProjectNavigationTab,
  ) {
    model.currentFilterItem = filterItem;
    model.currentTab = tab;

    if (model.isInit) {
      model.navigationChain = navigationChain;
      model.isInit = false;
      return;
    }
    model.searchMode = tab == ProjectNavigationTab.Search;
    model.navigationChain = navigationChain;
  }

  private async openOwnUser(model: ProjectNavigationModel, itemToOpen?: ProjectGroupVO): Promise<void> {
    model.currentSelectedItem = null;
    const userId = this.userModel.user.id;
    let currentRoot = model.rootProjectGroup.groups.find(
      (group) => group.groupOwner.userId === userId && group.groupName === ProjectNavigationModel.ROOT_NAME_MY_PROJECTS,
    );
    let openRootFolder = false;
    if (!currentRoot) {
      openRootFolder = true;
      currentRoot = new ProjectGroupVO();
      currentRoot.id = ProjectNavigationModel.ROOT_USER_ID;
      currentRoot.groupName = ProjectNavigationModel.ROOT_NAME_MY_PROJECTS;
      currentRoot.groupOwner = new UserInfoVO(userId);
      currentRoot.groups = [];
      currentRoot.projects = [];
      model.rootProjectGroup.groups.push(currentRoot);
    }
    if (!model.currentProjectFolder && !model.currentViewedProjectGroup) {
      model.currentProjectFolder = currentRoot;
    }
    if (itemToOpen) {
      model.clearCurrentViewedProjectGroup();
      model.currentProjectFolder = itemToOpen;
    }
    this.updateNavigationChain(
      model,
      this.buildNavigationChain(model, itemToOpen ?? model.currentProjectFolder),
      null,
      ProjectNavigationTab.Own,
    );
    if (currentRoot.childContentLoaded) {
      model.currentProjectFolder = currentRoot;
      return;
    }
    const loadedProjectGroup = await this.projectGroupLoadingService.loadOwnedBaseProjectGroupsWithPromise(0, model.currentModel.importType, false);
    try {
      this.addLoadedProjectGroupToData(model, currentRoot, loadedProjectGroup);
    } catch (ex) {
      Log.info(ex);
    }
    if (openRootFolder) {
      model.currentProjectFolder = currentRoot;
      model.clearCurrentViewedProjectGroup();
    }
  }

  private addLoadedProjectGroupToData(
    model: ProjectNavigationModel,
    targetProjectGroup: ProjectGroupVO,
    loadedProjectGroup: ProjectGroupVO,
  ): void {
    targetProjectGroup.groups.forEach((g) => {
      const loadedGroup: ProjectGroupVO = loadedProjectGroup.groups.find((lg) => lg.id === g.id);
      loadedGroup.projects = g.projects;
      loadedGroup.groups = g.groups;
      loadedGroup.childContentLoaded = g.childContentLoaded;
    });

    targetProjectGroup.groups = loadedProjectGroup.groups;
    targetProjectGroup.projects = loadedProjectGroup.projects;
    targetProjectGroup.childContentLoaded = true;
    targetProjectGroup.groups.forEach((g) => model.parentGroupMap.setValue(g.id, targetProjectGroup));
  }

  public async openProjectGroup(model: ProjectNavigationModel, selectedProjectGroup: ProjectGroupVO): Promise<void> {
    if (!ProjectGroupUtils.isConfigurationGroupType(selectedProjectGroup.groupType)) {
      this.navigateIntoOpenedFolder(model, selectedProjectGroup);
    }
    const isSearchRootProjectGroup = model.searchMode && selectedProjectGroup.id == ProjectNavigationModel.ROOT_ID;
    if (isSearchRootProjectGroup || selectedProjectGroup.childContentLoaded) {
      if (!ProjectGroupUtils.isConfigurationGroupType(selectedProjectGroup.groupType)) {
        model.currentProjectFolder = selectedProjectGroup;
      } else {
        model.currentChanged.next(model.currentProjectFolder);
      }
      return Promise.resolve();
    }

    const excludeHiddenGroups: boolean = !(model.currentModel.visibleHiddenProjects && model.currentTab === ProjectNavigationTab.Own);
    const loadedProjectGroup = model.compositeCheckBoxValue
      ? await this.articleProjectGroupLoadingService.loadArticleProjectGroupById(selectedProjectGroup.id, 1)
      : await this.projectGroupLoadingService.loadProjectGroupById(
          selectedProjectGroup.id,
          1,
          null,
          model.currentModel.importType,
          excludeHiddenGroups,
        );

    try {
      this.addLoadedProjectGroupToData(model, selectedProjectGroup, loadedProjectGroup);
      if (!ProjectGroupUtils.isConfigurationGroupType(selectedProjectGroup.groupType)) {
        model.currentProjectFolder = selectedProjectGroup;
      } else {
        model.currentChanged.next(model.currentProjectFolder);
      }
      this.selectProjectGroup(model, selectedProjectGroup, model.selectedProjectGroups.containsKey(selectedProjectGroup.id), true);
    } catch (ex) {
      Log.info(ex);
    }
    return Promise.resolve();
  }

  private navigateIntoOpenedFolder(model: ProjectNavigationModel, selectedProjectGroup: ProjectGroupVO) {
    let filterItem: FilterItem = null;
    if (!(model.currentFilterItem instanceof UserIdName)) {
      filterItem = model.currentFilterItem;
    } else if (selectedProjectGroup.groupOwner.userId !== this.userModel.user.id) {
      filterItem = new UserIdName(selectedProjectGroup.groupOwner.userId, selectedProjectGroup.groupOwner.username);
    }
    const shouldOpenSharedTab = selectedProjectGroup.groupOwner.userId !== this.userModel.user.id && !model.searchMode;
    this.updateNavigationChain(
      model,
      this.buildNavigationChain(model, selectedProjectGroup),
      filterItem,
      shouldOpenSharedTab ? ProjectNavigationTab.Shared : model.currentTab,
    );
  }

  private buildNavigationChain(model: ProjectNavigationModel, selectedProjectGroup: ProjectGroupVO): ProjectGroupVO[] {
    let chain = [];
    let currentGroup: ProjectGroupVO = selectedProjectGroup;
    while (currentGroup && currentGroup.id !== ProjectNavigationModel.ROOT_ID) {
      chain = [currentGroup, ...chain];
      currentGroup = model.parentGroupMap.getValue(currentGroup.id);
    }
    return chain;
  }

  /** If an opened folder is already in the navigation chain, returns projectgroup that follows it in the chain. For example if we have the following navigationChain:
   *  [My projects] > [My Base Folder] > [My Subfolder] > [My SubSubFolder] and we check [My Base Folder], the Group [My Subfolder] will be returned*/
  public findFollowingGroupInChain(
    model: ProjectNavigationModel,
    projectGroup: ProjectGroupVO,
    currentViewedProjectGroupIndex: number,
  ): ProjectGroupVO | undefined {
    if (!model.navigationChain) return;

    const groupIndexInNavigationChain = model.navigationChain.findIndex((g) => projectGroup.id == g.id);
    const isLastInChain = groupIndexInNavigationChain == model.navigationChain.length - 1;

    if (isLastInChain && currentViewedProjectGroupIndex !== -1) {
      return model.navigationChain[groupIndexInNavigationChain].groups[currentViewedProjectGroupIndex];
    }

    if (isLastInChain && model.currentViewedProjectGroup) {
      return model.navigationChain[groupIndexInNavigationChain + 1];
    }

    if (groupIndexInNavigationChain == -1 || isLastInChain) return;

    return model.navigationChain[groupIndexInNavigationChain + 1];
  }

  public selectProjectGroup(
    model: ProjectNavigationModel,
    projectGroup: ProjectGroupVO,
    select: boolean,
    isOnChildContentLoaded = false,
  ): void {
    if (select) {
      model.selectedProjectGroups.setValue(projectGroup.id, projectGroup);
    } else {
      model.selectedProjectGroups.remove(projectGroup.id);
      for (const project of projectGroup.projects) {
        model.selectedProjects.remove(project.id);
      }
    }
    if (select || !isOnChildContentLoaded) {
      const projectGroupsToProcess: ProjectGroupVO[] = select
        ? [projectGroup]
        : this.getProjectGroupsFromRootGroups(model, projectGroup.id);
      projectGroupsToProcess.forEach((group) => {
        group.projects.forEach((p) => this.selectProject(model, p, select));
        group.groups.forEach((g) => this.selectProjectGroup(model, g, select));
      });
    }
  }

  private getProjectGroupsFromRootGroups(model: ProjectNavigationModel, groupId: number): ProjectGroupVO[] {
    const result: ProjectGroupVO[] = [];
    if (!groupId) {
      return result;
    }
    for (const rootGroup of model.rootProjectGroup.groups) {
      const group: ProjectGroupVO = ProjectGroupUtils.findProjectGroupInATree([rootGroup], groupId);
      if (group) {
        result.push(group);
      }
    }
    if (model.rootSearchProjectGroup) {
      const searchGroup: ProjectGroupVO = ProjectGroupUtils.findProjectGroupInATree(model.rootSearchProjectGroup.groups, groupId);
      if (searchGroup) {
        result.push(searchGroup);
      }
    }
    return result;
  }

  public selectProject(model: ProjectNavigationModel, projectHead: ProjectHeadVO, select: boolean): void {
    if (select) {
      model.selectedProjects.setValue(projectHead.id, projectHead);
    } else {
      model.selectedProjects.remove(projectHead.id);
    }

    /* If a project is deselected, also deselect its parent projectGroup -
    projectGroups should only be selected if all children are. */
    if (!select && projectHead.projectGroup) {
      model.selectedProjectGroups.remove(projectHead.projectGroup.id);
    }
  }

  public selectProjectForMultipleQuoteRequest(model: ProjectNavigationModel, project: ProjectHeadVO, select: boolean): void {
    if (select) {
      model.selectedProjects.setValue(project.id, project);
      model.selectedProjects.forEach((key: number, value: ProjectVO) => {
        if (value.projectGroup.id !== project.projectGroup.id) {
          model.selectedProjects.remove(key);
          model.selectedProjectGroups.remove(value.projectGroup.id);
        }
      });
    } else {
      model.selectedProjects.remove(project.id);
      if (model.selectedProjects.isEmpty()) {
        model.selectedProjectGroups.clear();
      }
    }
  }

  /** Returns "all" if all projects of a group are selected, "some" if not all but > 0 are, else "none" */
  public updateSelectionStateForProjectGroup(model: ProjectNavigationModel, projectGroup?: ProjectGroupVO): "all" | "some" | "none" {
    if (!projectGroup) {
      return "none";
    }

    if (model.selectedProjectGroups.containsKey(projectGroup.id)) {
      return "all";
    }

    const selectedProjectOfCurrentGroup = model.selectedProjects.values().filter((_) => projectGroup.projects.includes(_));

    if (selectedProjectOfCurrentGroup.length == 0) {
      return "none";
    }

    if (selectedProjectOfCurrentGroup.length == projectGroup.projects.length) {
      // if all projects of a ProjectGroup are selected, automatically also select itself
      model.selectedProjectGroups.setValue(projectGroup.id, projectGroup);
      return "all";
    }
    return "some";
  }

  public openMyAssignedProjects(model: ProjectNavigationModel): Promise<void> {
    model.currentSelectedItem = null;
    model.clearCurrentViewedProjectGroup();
    const currentRoot = model.rootProjectGroup.groups.find((group) => group.groupName === ProjectNavigationModel.ROOT_NAME_MY_ASSIGNED);
    if (!currentRoot) {
      return Promise.reject(new Error("My assigned projects group not found"));
    }

    this.updateNavigationChain(model, [currentRoot], null, ProjectNavigationTab.MyAssigned);
    model.currentProjectFolder = currentRoot;
    model.clearCurrentViewedProjectGroup();

    return Promise.resolve();
  }

  public async openRecentlyUsedProjects(model: ProjectNavigationModel): Promise<void> {
    model.currentSelectedItem = null;
    model.clearCurrentViewedProjectGroup();
    if (model.compositeCheckBoxValue) {
      this.openProjectGroup(model, model.rootProjectGroup.groups[0]);
      return;
    }

    let currentRoot = model.rootProjectGroup.groups.find((group) => group.groupName === ProjectNavigationModel.ROOT_NAME_RECENTLY_USED);
    if (currentRoot == null) {
      const userBase = new ProjectGroupVO();
      userBase.id = ProjectNavigationModel.ROOT_USER_ID;
      userBase.groupName = ProjectNavigationModel.ROOT_NAME_RECENTLY_USED;
      userBase.groupOwner = new UserInfoVO(this.userModel.user.id, this.userModel.user.name);
      userBase.groups = [];
      userBase.projects = [];
      model.rootProjectGroup.groups.push(userBase);
      currentRoot = userBase;
    }

    this.updateNavigationChain(model, [currentRoot], null, ProjectNavigationTab.RecentlyUsed);

    if (currentRoot.childContentLoaded) {
      model.currentProjectFolder = currentRoot;
      return Promise.resolve();
    }

    try {
      currentRoot.projects = await lastValueFrom(this.projectDataService.loadRecentlyUsedProjects());
      currentRoot.childContentLoaded = true;
      model.currentProjectFolder = currentRoot;
      model.clearCurrentViewedProjectGroup();
      model.currentSelectedItem = currentRoot.projects[0];
    } catch(e) {
      Log.error(e);
    }
  }

  public openPinnedProjects(model: ProjectNavigationModel): Promise<void> {
    model.currentSelectedItem = null;
    model.clearCurrentViewedProjectGroup();
    if (model.compositeCheckBoxValue) {
      this.openProjectGroup(model, model.rootProjectGroup.groups[0]);
      return;
    }

    let currentRoot = model.rootProjectGroup.groups.find((group) => group.groupName === ProjectNavigationModel.ROOT_NAME_PINNED);
    if (currentRoot == null) {
      const userBase = new ProjectGroupVO();
      userBase.id = ProjectNavigationModel.ROOT_USER_ID;
      userBase.groupName = ProjectNavigationModel.ROOT_NAME_PINNED;
      userBase.groupOwner = new UserInfoVO(this.userModel.user.id);
      userBase.groups = [];
      userBase.projects = [];
      model.rootProjectGroup.groups.push(userBase);
      currentRoot = userBase;
    }

    this.updateNavigationChain(model, [currentRoot], null, ProjectNavigationTab.Pinned);

    if (currentRoot.childContentLoaded) {
      model.currentProjectFolder = currentRoot;
      return Promise.resolve();
    }

    return this.projectGroupLoadingService
      .loadPinnedProjectGroups(model.currentModel.importType)
      .then((projectGroups: ProjectGroupVO[]) => {
        projectGroups.forEach((group: ProjectGroupVO) => {
          model.pinnedGroupsParentGroupMap.setValue(group.id, group.parentGroup);
        });
        currentRoot.groups = projectGroups;
        currentRoot.childContentLoaded = true;
        model.clearCurrentViewedProjectGroup();
        model.currentProjectFolder = currentRoot;
      })
      .catch((e) => Log.info(e));
  }

  public async openSearch(model: ProjectNavigationModel): Promise<void> {
    model.currentTab = ProjectNavigationTab.Search;
    model.currentProjectFolder = null;
    model.clearCurrentViewedProjectGroup();
    this.updateNavigationChain(model, null, null, ProjectNavigationTab.Search);
  }

  public async openSharedUser(model: ProjectNavigationModel, userId: UserIdName = null): Promise<void> {
    model.currentSelectedItem = null;
    model.clearCurrentViewedProjectGroup();
    if (userId === null) {
      if (model.visibleSharedUsers.length > 0) {
        userId = model.visibleSharedUsers[0];
      } else {
        return this.openEmptySharedGroup(model);
      }
    }

    let currentRoot = model.rootProjectGroup.groups.find((projectGroup) => projectGroup.groupOwner.userId === userId.id);
    if (currentRoot == null) {
      const userBase = new ProjectGroupVO();
      userBase.id = ProjectNavigationModel.ROOT_USER_ID;
      userBase.groupName = ProjectNavigationModel.ROOT_NAME_SHARED;
      userBase.groupOwner = new UserInfoVO(userId.id, userId.name);
      userBase.groups = [];
      userBase.projects = [];
      model.rootProjectGroup.groups.push(userBase);
      currentRoot = userBase;
    }

    this.updateNavigationChain(model, [currentRoot], userId, ProjectNavigationTab.Shared);

    if (currentRoot.childContentLoaded) {
      model.currentProjectFolder = currentRoot;
      return Promise.resolve();
    }

    const loadedProjectGroup = model.compositeCheckBoxValue
      ? await this.articleProjectGroupLoadingService.loadArticleProjectGroupById(userId.id, 1)
      : await this.projectGroupLoadingService.loadSharedBaseProjectGroups(userId.id, 0, model.currentModel.importType);

    try {
      this.addLoadedProjectGroupToData(model, currentRoot, loadedProjectGroup);
      model.currentProjectFolder = currentRoot;
    } catch (ex) {
      Log.info(ex);
    }
    model.clearCurrentViewedProjectGroup();
    return Promise.resolve();
  }

  public openEmptySharedGroup(model: ProjectNavigationModel): Promise<void> {
    let currentRoot: ProjectGroupVO = model.rootProjectGroup.groups.find((projectGroup) => {
      return projectGroup.groupName === ProjectNavigationModel.ROOT_NAME_SHARED && projectGroup.groupOwner.userId === null;
    });

    if (!currentRoot) {
      const userBase = new ProjectGroupVO();
      userBase.id = ProjectNavigationModel.ROOT_USER_ID;
      userBase.groupName = ProjectNavigationModel.ROOT_NAME_SHARED;
      userBase.groupOwner = new UserInfoVO();
      userBase.groups = [];
      userBase.projects = [];
      userBase.isSharedReadOnly = true;
      model.rootProjectGroup.groups.push(userBase);
      currentRoot = userBase;
    }

    this.updateNavigationChain(model, [currentRoot], null, ProjectNavigationTab.Shared);
    model.currentProjectFolder = currentRoot;
    return Promise.resolve();
  }

  public isAddProjectGroupDisabled(model: ProjectNavigationModel): boolean {
    if (model.currentViewedProjectGroup) {
      return true;
    }
    const isAddingProjectGroupDisabled: boolean = this.isAddingGroupDisabled(model);
    if (isAddingProjectGroupDisabled && model.currentProjectFolder) {
      return !ProjectGroupUtils.isDefaultImportProjectGroupType(model.currentProjectFolder.groupType) ||
        !this.userModel.user.hasPermission("ACCESS_DEFAULT_IMPORT_PROJECT_GROUP");
    } else {
      return isAddingProjectGroupDisabled
    }
  }

  private isAddingGroupDisabled(model: ProjectNavigationModel): boolean {
    if (!this.userModel.user?.hasPermission('SAVE_PROJECT_GROUPS')) {
      return true;
    }
    if (model.searchMode) {
      return true;
    }
    if ([
      ProjectNavigationTab.Search,
      ProjectNavigationTab.Pinned,
      ProjectNavigationTab.RecentlyUsed,
      ProjectNavigationTab.MyAssigned,
    ].includes(model.currentTab)) {
      return true;
    }
    if (ProjectNavigationTab.Shared === model.currentTab) {
      // Disable adding to root when in 'shared with me'
      if ([ProjectNavigationModel.ROOT_ID, ProjectNavigationModel.ROOT_USER_ID].includes(model.currentProjectFolder?.id)) {
        return true;
      }
      // Disable adding if folder or group is not shared or shared readonly in 'shared with me'
      if (model.currentProjectFolder?.isSharedReadOnly !== false) {
        return true;
      }
    }
    if (!model.currentProjectFolder) {
      return false;
    }
    if (model.currentProjectFolder.groupType > 0) {
      return true;
    }
    return model.currentProjectFolder.isSharedReadOnly;
  }

  public async performSearch(model: ProjectNavigationModel, searchText: string, toggleExtendedSearch: boolean): Promise<void> {
    const actualValue: string = searchText.trim();
    if (StringUtils.isEmpty(actualValue)) {
      if (model.searchMode) {
        this.resetSearch(model);
      }
      return;
    }

    try {
      let projectGroups: ProjectGroupVO[];
      if (model.compositeCheckBoxValue) {
        projectGroups = await this.articleProjectGroupLoadingService.searchArticleProjectGroups(actualValue, toggleExtendedSearch);
      } else {
        projectGroups = await this.projectGroupLoadingService.searchProjectGroups(
          actualValue,
          model.currentModel.importType,
          toggleExtendedSearch,
        );
      }
      this.searchSuccessHandler(model, actualValue, projectGroups);
    } catch (error) {
      this.searchFailureHandler(model);
    }
  }

  private searchSuccessHandler(model: ProjectNavigationModel, actualValue: string, projectGroups: ProjectGroupVO[]) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    model.searchText = actualValue;
    this.addSearchResultToModel(model, projectGroups, actualValue);
    model.sharedFilter = ``;
  }

  private searchFailureHandler(model: ProjectNavigationModel) {
    if (model.searchMode) {
      this.resetSearch(model);
    }
  }

  private resetSearch(model: ProjectNavigationModel): void {
    model.searchMode = false;
    model.searchText = "";
    model.sharedFilter = ``;
    this.openPreselectedProjectGroup(model);
    model.resetSelections();
  }

  private addSearchResultToModel(model: ProjectNavigationModel, projectGroups: ProjectGroupVO[], searchText: string): void {
    let filteredGroups: ProjectGroupVO[] = this.excludeDefaultUserGroups(projectGroups);
    // sollte keinen Sinn machen, da nur geladen werden sollte was man auch sehen darf!!!!!!!!!
    if (!model.currentModel.visibleSharedProjects) {
      filteredGroups = filteredGroups.filter((item) => item.groupOwner.userId === this.userModel.user.id);
    }
    filteredGroups = this.filterOnlySearchedGroups(filteredGroups, searchText);

    model.rootSearchProjectGroup = this.createRootProjectGroup(filteredGroups);
    model.resetSelections();
    model.updateCurrentFilterItemAvailability(model.rootSearchProjectGroup);
    model.searchMode = true;
    model.searchText = searchText;
    model.currentProjectFolder = model.rootSearchProjectGroup;
    model.currentFilterItem = null;
    model.navigationChain = [];
  }

  private filterOnlySearchedGroups(projectGroups: ProjectGroupVO[], searchText: string): ProjectGroupVO[] {
    return projectGroups.filter(
      (g) => StringUtils.containsString(g.groupName, searchText, true) || (g.projects != null && g.projects.length > 0),
    );
  }

  public isRequestFinalizeProjectEnabled(projectHead: ProjectHeadVO): boolean {
    if (projectHead.finalizationState == null) {
      return false;
    }
    if (projectHead.isSharedReadOnly) {
      return false;
    }
    if (projectHead.finalizationState < ProjectStatesDef.FINALIZATION_STATE_REQUESTED) {
      return true;
    } else {
      return false;
    }
  }

  public toggleGroupPinning(model: ProjectNavigationModel, projectGroup: ProjectGroupVO): Observable<boolean> {
    return this.shouldOverideProjectsPinning(projectGroup).pipe(
      switchMap((overwriteProjects: boolean) => {
        const temporaryLinkGUIDs: string[] = this.getProjectsTemporaryLinkGUIDs(projectGroup);
        return this.projectGroupDataService
          .changeProjectGroupPinning(projectGroup.id, !projectGroup.isPinned, overwriteProjects, temporaryLinkGUIDs)
          .pipe(
            map((updatedProjectGroup: ProjectGroupVO) => {
              if (updatedProjectGroup) {
                const isPinned: boolean = updatedProjectGroup.isPinned;
                this.updateRootGroupsOnGroupPinningChanged(model, updatedProjectGroup, isPinned, overwriteProjects);
              }
              return !!updatedProjectGroup;
            }),
          );
      }),
    );
  }

  private shouldOverideProjectsPinning(projectGroup: ProjectGroupVO): Observable<boolean> {
    if (projectGroup.isPinned) {
      return fromPromise(
        this.alertHelper.showAlert(
          "projectNavigation.alert.title.overwrite_pinning",
          "projectNavigation.alert.message.overwrite_pinning",
          null,
          [AlertButton.YES, AlertButton.NO],
        ),
      ).pipe(map((answer) => answer === AlertButton.YES));
    } else {
      return of(true);
    }
  }

  private getProjectsTemporaryLinkGUIDs(projectGroup: ProjectGroupVO): string[] {
    const result: string[] = [];
    if (!projectGroup || !projectGroup.projects) {
      return result;
    }
    for (const projectHead of projectGroup.projects) {
      if (projectHead.temporaryLinkGUID) {
        result.push(projectHead.temporaryLinkGUID);
      }
    }
    return result;
  }

  private updateRootGroupsOnGroupPinningChanged(
    model: ProjectNavigationModel,
    projectGroup: ProjectGroupVO,
    isPinned: boolean,
    overwriteProjects: boolean,
  ): void {
    const projectGroupsInRootGroups: ProjectGroupVO[] = this.getProjectGroupsFromRootGroups(model, projectGroup.id);
    projectGroupsInRootGroups.forEach((group) => {
      group.isPinned = isPinned;
    });
    if (overwriteProjects) {
      projectGroupsInRootGroups.forEach((group) => {
        group.projects.forEach((project) => (project.isPinned = isPinned));
      });
    }
    this.updateRootPinnedGroupOnGroupPinningChanged(model, projectGroup, isPinned, overwriteProjects);
  }

  private updateRootPinnedGroupOnGroupPinningChanged(
    model: ProjectNavigationModel,
    projectGroup: ProjectGroupVO,
    isPinned: boolean,
    overwriteProjects: boolean,
  ): void {
    const rootPinnedGroup: ProjectGroupVO = model.rootProjectGroup.groups.find(
      (group) => group.groupName === ProjectNavigationModel.ROOT_NAME_PINNED,
    );
    if (rootPinnedGroup) {
      const pinnedGroup: ProjectGroupVO = rootPinnedGroup.groups.find((g) => g.id === projectGroup.id);
      if (pinnedGroup) {
        if (isPinned) {
          pinnedGroup.projects = projectGroup.projects.filter((p) => p.isPinned);
        } else {
          if (overwriteProjects || !projectGroup.projects.some((p) => p.isPinned)) {
            rootPinnedGroup.groups = rootPinnedGroup.groups.filter((group) => group.id !== projectGroup.id);
          }
        }
      } else if (isPinned) {
        projectGroup.childContentLoaded = true;
        model.pinnedGroupsParentGroupMap.setValue(projectGroup.id, projectGroup.parentGroup);
        rootPinnedGroup.groups.push(projectGroup);
      }
    }
  }

  public toggleProjectPinning(model: ProjectNavigationModel, project: ProjectHeadVO): Observable<boolean> {
    return this.projectDataService
      .changeProjectPinning(project.id, project.currentVersion, !project.isPinned, project.temporaryLinkGUID)
      .pipe(
        tap((isSuccess: boolean) => {
          if (isSuccess) {
            const isPinned: boolean = !project.isPinned;
            this.updateRootGroupsOnProjectPinningChanged(model, project, isPinned);
          }
        }),
      );
  }

  private updateRootGroupsOnProjectPinningChanged(model: ProjectNavigationModel, project: ProjectHeadVO, isPinned: boolean): void {
    if(!project.projectGroup) { //in search mode, the projects have no project group set
      project.isPinned = isPinned;
    }

    const projectGroupsInRootGroups: ProjectGroupVO[] = this.getProjectGroupsFromRootGroups(model, project.projectGroup.id);
    for (const group of [model.currentProjectFolder, ...projectGroupsInRootGroups]) {
      const projectInRootGroup: ProjectHeadVO = group.projects.find((p) => p.id === project.id);
      if (projectInRootGroup) {
        projectInRootGroup.isPinned = isPinned;
      }
    }
    this.updateRootPinnedGroupOnProjectPinningChanged(model, project, isPinned);
  }

  private updateRootPinnedGroupOnProjectPinningChanged(model: ProjectNavigationModel, projectHead: ProjectHeadVO, isPinned: boolean): void {
    const rootPinnedGroup: ProjectGroupVO = model.rootProjectGroup.groups.find((g) => g.groupName === ProjectNavigationModel.ROOT_NAME_PINNED);
    if (rootPinnedGroup) {
      const pinnedGroup: ProjectGroupVO = rootPinnedGroup.groups.find((g) => g.id === projectHead.projectGroup.id);
      if (pinnedGroup) {
        if (isPinned) {
          pinnedGroup.projects.push(projectHead);
        } else {
          pinnedGroup.projects = pinnedGroup.projects.filter((p) => p.id !== projectHead.id);
          if (!pinnedGroup.isPinned && ArrayUtils.isEmpty(pinnedGroup.projects)) {
            rootPinnedGroup.groups = rootPinnedGroup.groups.filter((g) => g.id !== pinnedGroup.id);
          }
        }
      } else if (isPinned) {
        const newPinnedGroup = CloneUtils.cloneProjectGroupVO(projectHead.projectGroup);
        newPinnedGroup.projects = [projectHead];
        newPinnedGroup.childContentLoaded = true;
        model.pinnedGroupsParentGroupMap.setValue(newPinnedGroup.id, newPinnedGroup.parentGroup);
        rootPinnedGroup.groups.push(newPinnedGroup);
      }
    }
  }

  public async deleteConfiguration(model: ProjectNavigationModel, configurationToRemove: ProjectHeadVO): Promise<boolean> {
    if (configurationToRemove) {
      const deletionExecuted = await this.projectService.deleteConfiguration(configurationToRemove);
      if (!deletionExecuted) {
        return false;
      }

      model.selectedProjects.remove(configurationToRemove.id);
      const projectGroupsInRootGroups: ProjectGroupVO[] = this.getProjectGroupsFromRootGroups(model, configurationToRemove.projectGroup.id);

      for (const group of projectGroupsInRootGroups) {
        const index: number = group.projects.findIndex((p) => p.id === configurationToRemove.id);
        if (index !== -1) {
          group.projects.splice(index, 1);
        }
      }
      this.removeConfigurationFromRecentlyUsed(model, configurationToRemove);

      model.currentChanged.next(model.currentProjectFolder);
      model.currentSelectedItem = null;
      return true;
    }
  }

  private removeConfigurationFromRecentlyUsed(model: ProjectNavigationModel, configurationToRemove: ProjectHeadVO): void {
    const rootRecentlyUsedGroup: ProjectGroupVO = model.rootProjectGroup.groups.find(
      (group) => group.groupName === ProjectNavigationModel.ROOT_NAME_RECENTLY_USED,
    );
    if (rootRecentlyUsedGroup) {
      const index: number = rootRecentlyUsedGroup.projects.findIndex((p) => p.id === configurationToRemove.id);
      if (index !== -1) {
        rootRecentlyUsedGroup.projects.splice(index, 1);
      }
    }
  }

  public initChangeOwnerDialog(model, item: ProjectGroupVO | ProjectHeadVO): void {
    this.projectComponentPresenter.openReassignComponent(item).then((result) => {
      if (item instanceof ProjectHeadVO) {
        this.changeProjectOwnerSuccess(model, item, result as ProjectHeadVO);
      } else {
        this.changeProjectGroupOwnerSuccess(model, item, result as ProjectGroupVO);
      }
    });
  }

  public changeProjectOwnerSuccess(model: ProjectNavigationModel, oldItem: ProjectHeadVO, newItem: ProjectHeadVO): void {
    if (!oldItem || !newItem) {
      return;
    }
    let originGroup = ProjectGroupUtils.findProjectGroupInATree(model.rootProjectGroup.groups, oldItem.projectGroup.id);
    if (originGroup) {
      originGroup.childContentLoaded = false;
    }
    if (model.searchMode) {
      originGroup = ProjectGroupUtils.findProjectGroupInATree(model.rootSearchProjectGroup.groups, oldItem.projectGroup.id);
      this.removeOldProjectFromGroups(oldItem, originGroup);
      const currentGroup = model.currentProjectFolder.groups.find((g) => g.id === originGroup?.id);
      this.removeOldProjectFromGroups(oldItem, currentGroup);
    }
    if (originGroup) {
      this.openProjectGroup(model, originGroup);
    }
    this.setCurrentChangedFolder(model, newItem.projectGroup.parentGroup);
  }

  private removeOldProjectFromGroups(oldProject: ProjectHeadVO, projectGroup: ProjectGroupVO): void {
    if (projectGroup) {
      const index = projectGroup.projects.findIndex((g) => g.id === oldProject.id);
      if (index !== -1) {
        projectGroup.projects.splice(index, 1);
      }
    }
  }

  private changeProjectGroupOwnerSuccess(model: ProjectNavigationModel, oldItem: ProjectGroupVO, newItem: ProjectGroupVO): void {
    if (!oldItem || !newItem) {
      return;
    }
    if (!oldItem.parentGroup) {
      if (model.searchMode) {
        this.removeOldGroupFromGroups(oldItem, model.rootProjectGroup);
      }
      const currentOrigin: ProjectGroupVO = model.navigationChain[model.navigationChain.length - 1];
      this.removeOldGroupFromGroups(oldItem, currentOrigin);
      model.currentProjectFolder = currentOrigin;
    } else {
      let originGroup: ProjectGroupVO = ProjectGroupUtils.findProjectGroupInATree(model.rootProjectGroup.groups, oldItem.parentGroup.id);
      if (originGroup) {
        originGroup.childContentLoaded = false;
      }
      if (model.searchMode) {
        originGroup = ProjectGroupUtils.findProjectGroupInATree(model.rootSearchProjectGroup.groups, oldItem.parentGroup.id);
        this.removeOldGroupFromGroups(oldItem, originGroup);
      }
      model.navigationChain.pop();
      this.openProjectGroup(model, originGroup);
    }
    this.setCurrentChangedFolder(model, newItem.parentGroup);
  }

  private removeOldGroupFromGroups(oldGroup: ProjectGroupVO, projectGroup: ProjectGroupVO): void {
    if (projectGroup) {
      const index = projectGroup.groups.findIndex((g) => g.id === oldGroup.id);
      if (index !== -1) {
        projectGroup.groups.splice(index, 1);
      }
    }
  }

  private setCurrentChangedFolder(model: ProjectNavigationModel, newItemParentGroup: ProjectGroupVO): void {
    const originGroup: ProjectGroupVO = ProjectGroupUtils.findProjectGroupInATree(model.rootProjectGroup.groups, newItemParentGroup.id);
    if (originGroup) {
      originGroup.childContentLoaded = false;
    }
    if (model.searchMode) {
      model.currentChanged.next(model.currentProjectFolder);
    }
  }

  public moveOrCopyDialogClosed(model: ProjectNavigationModel): void {
    model.currentChanged.next(model.currentProjectFolder);
  }

  public async moveOperationSuccess(
    model: ProjectNavigationModel,
    oldItem: ProjectGroupVO | ProjectHeadVO,
    movedItem: ProjectGroupVO | ProjectHeadVO,
    targetFilterGroup: ProjectNavigationTab,
  ): Promise<void> {
    await this.moveOrCopyOperationSuccess(model, oldItem, movedItem, OperationType.MOVE, targetFilterGroup);
  }

  public async copyOperationSuccess(
    model: ProjectNavigationModel,
    oldItem: ProjectGroupVO | ProjectHeadVO,
    copiedItem: ProjectGroupVO | ProjectHeadVO,
    targetFilterGroup: ProjectNavigationTab,
  ): Promise<void> {
    await this.moveOrCopyOperationSuccess(model, oldItem, copiedItem, OperationType.COPY, targetFilterGroup);
  }

  private async moveOrCopyOperationSuccess(
    model: ProjectNavigationModel,
    oldItem: ProjectGroupVO | ProjectHeadVO,
    newItem: ProjectGroupVO | ProjectHeadVO,
    operationType: OperationType,
    targetFilterGroup: ProjectNavigationTab,
  ): Promise<void> {
    if (!oldItem || !newItem || (operationType !== OperationType.MOVE && operationType !== OperationType.COPY)) {
      return;
    }
    if (oldItem instanceof ProjectHeadVO && newItem instanceof ProjectHeadVO) {
      await this.moveOrCopyProjectSuccess(model, oldItem, newItem, operationType);
      if (
        this.projectModel.currentProject &&
        newItem.id === this.projectModel.currentProject.id &&
        !!this.projectModel.currentProject.isArticleProject === !!model.compositeCheckBoxValue
      ) {
        this.projectModel.currentProject.projectGroup = newItem.projectGroup;
        this.projectModel.currentProject.currentVersion = newItem.currentVersion;
        this.projectModel.currentProject.currentVersionDisplayString = newItem.currentVersionDisplayString;
        this.projectModel.currentProject.documentVersion = newItem.documentVersion;
        this.projectModel.currentProject.finalizationState = newItem.finalizationState;
        this.projectSettingsService.notifyProjectGroupUpdatedSuccess(
          this.projectModel.currentProject.projectGroup,
          this.projectModel.currentProject.isArticleProject,
        );
      }
    } else if (oldItem instanceof ProjectGroupVO && newItem instanceof ProjectGroupVO) {
      await this.moveOrCopyProjectGroupSuccess(model, oldItem, newItem, operationType, targetFilterGroup);
    }
  }

  private async moveOrCopyProjectSuccess(
    model: ProjectNavigationModel,
    oldProject: ProjectHeadVO,
    newProject: ProjectHeadVO,
    operationType: OperationType,
  ): Promise<void> {
    const sourceParentGroup: ProjectGroupVO = model.currentProjectFolder.groups.find((g) => g.id === oldProject.projectGroup.id);
    if (operationType === OperationType.MOVE) {
      const index: number = sourceParentGroup.projects.findIndex((p) => p.id === oldProject.id);
      if (index !== -1) {
        sourceParentGroup.projects.splice(index, 1);
      }
    }

    let targetParentGroup: ProjectGroupVO = ProjectGroupUtils.findProjectGroupInATree(model.rootProjectGroup.groups, newProject.projectGroup.id);
    if (targetParentGroup) {
      targetParentGroup.childContentLoaded = false;
      targetParentGroup.projects = [];
    } else if (model.searchMode) {
      targetParentGroup = ProjectGroupUtils.findProjectGroupInATree(model.rootSearchProjectGroup.groups, newProject.projectGroup.id);
      targetParentGroup.projects.push(newProject);
    }
    if (!targetParentGroup) {
      this.openProjectGroup(model, sourceParentGroup);
      return;
    }

    model.setCurrentViewedProjectGroup(targetParentGroup);

    const groupToOpen: ProjectGroupVO = model.parentGroupMap.getValue(targetParentGroup.id);
    if (groupToOpen) {
      await this.openGroup(model, groupToOpen);
    }

    model.selectItem(newProject, this.projectModel);

    await this.openProjectGroup(model, targetParentGroup);
    model.scrollToSelected.next();
  }

  private async moveOrCopyProjectGroupSuccess(
    model: ProjectNavigationModel,
    oldProjectGroup: ProjectGroupVO,
    newProjectGroup: ProjectGroupVO,
    operationType: OperationType,
    targetFilterGroup: ProjectNavigationTab,
  ): Promise<void> {
    const sourceParentGroup: ProjectGroupVO = model.currentProjectFolder;
    if (operationType === OperationType.MOVE) {
      const index: number = sourceParentGroup.groups.findIndex((g) => g.id === oldProjectGroup.id);
      if (index !== -1) {
        sourceParentGroup.groups.splice(index, 1);
      }
    }

    const targetParentGroup: ProjectGroupVO = model.findParentGroup(newProjectGroup, this.userModel.user);
    if (targetParentGroup) {
      targetParentGroup.groups.push(newProjectGroup);
      model.parentGroupMap.setValue(newProjectGroup.id, targetParentGroup);

      if (!model.searchMode) {
        model.currentTab = targetFilterGroup;
        await this.openProjectGroup(model, targetParentGroup);
        model.selectItem(newProjectGroup, this.projectModel);
        model.scrollToSelected.next();
      }
    } else {
      model.currentProjectFolder = sourceParentGroup;
    }
  }

  public renameProjectSuccess(model: ProjectNavigationModel, renamedProject: ProjectHeadVO): void {
    if (!renamedProject || !renamedProject.projectGroup) {
      return;
    }
    const projectGroupsInRootGroups: ProjectGroupVO[] = this.getProjectGroupsFromRootGroups(model, renamedProject.projectGroup.id);
    projectGroupsInRootGroups.forEach((group) => {
      const projectInRootGroup: ProjectHeadVO = group.projects.find((p) => p.id === renamedProject.id);
      if (projectInRootGroup) {
        projectInRootGroup.name = renamedProject.name;
      }
    });
    model.currentChanged.next(model.currentProjectFolder);
  }

  public editGroup(model: ProjectNavigationModel, projectGroup: ProjectGroupVO): void {
    if (!projectGroup) {
      return;
    }

    if (ProjectGroupUtils.isConfigurationGroupType(projectGroup.groupType)) {
      const projectUpdatedSuccessHandler = (data: ProjectSettingsNotification) => {
        this.projectGroupUpdatedSubscription.unsubscribe();

        if (data && data.group) {
          const projectGroupsInRootGroups: ProjectGroupVO[] = this.getProjectGroupsFromRootGroups(model, projectGroup.id);
          projectGroupsInRootGroups.forEach((group) => {
            group.groupName = data.group.groupName;
            group.options = data.group.options;
          });
        }
      };

      const projectUpdatedFailureHandler = () => {
        this.projectGroupUpdatedSubscription.unsubscribe();
      };

      this.projectGroupUpdatedSubscription = this.projectSettingsService.projectGroupUpdated$.subscribe(
        (notification: ProjectSettingsNotification) => {
          return notification.success ? projectUpdatedSuccessHandler(notification) : projectUpdatedFailureHandler();
        },
      );

      this.projectComponentPresenter.openEditProjectSettingsComponent(projectGroup, model.compositeCheckBoxValue);
    } else {
      this.projectComponentPresenter
        .openEditProjectGroupPopup("editProjectGroup.title.edit", ProjectGroupUtils.FOLDER, projectGroup, null, model.compositeCheckBoxValue)
        .then((group) => {
          if (group) {
            this.updateDefaultProjectGroup(model, group);
          }
        });
    }
  }

  /* add a new projectGroup at the current location and select it */
  public async newProjectGroup(model: ProjectNavigationModel): Promise<void> {
    // Only set parentGroup if not in root
    const parentGroupId = model.currentProjectFolder?.id;
    const saveWithParentGroup = parentGroupId !== ProjectNavigationModel.ROOT_USER_ID &&
      parentGroupId !== ProjectNavigationModel.ROOT_ID;

    let group = new ProjectGroupVO();
    group.guid = StringUtils.createUuid();
    group.groupType = ProjectGroupUtils.PROJECT;
    group.parentGroup = saveWithParentGroup ? model.currentProjectFolder : undefined;
    group.groupOwner = new UserInfoVO(this.userModel.userProfile.user.id, this.userModel.userProfile.user.name);
    group.isHidden = false;
    group.groups = [];

    group = await this.projectComponentPresenter.openEditProjectSettingsComponent(group, false);

    try {
      group = await this.projectGroupService.saveProjectGroup(model.compositeCheckBoxValue, group);

      model.attachProjectGroup(group, this.userModel.user);
      model.selectItem(group, this.projectModel);
      model.scrollToSelected.next();
    } catch (error) {
      if (error.status === HttpStatusCode.Conflict) {
        // project group with the same name already exists
        await this.showErrorMessage(error, "save.title.error_saving_projectGroup", "save.message.error_projectGroup_name_exists");
      }
    }
  }

  /* add a new group folder at the current location and select it */
  public newFolder(model: ProjectNavigationModel): void {
    const parentGroup: ProjectGroupVO = model.currentProjectFolder;
    const title: string = "editProjectGroup.title.new";
    this.projectComponentPresenter
      .openEditProjectGroupPopup(title, ProjectGroupUtils.FOLDER, null, parentGroup, model.compositeCheckBoxValue, false)
      .then((group) => {
        if (group) {
          model.attachProjectGroup(group, this.userModel.user);
          model.selectItem(group, this.projectModel);
          model.scrollToSelected.next();
        }
      });
  }

  private updateDefaultProjectGroup(model: ProjectNavigationModel, projectGroup: ProjectGroupVO): void {
    const currentGroup: ProjectGroupVO = model.currentProjectFolder;
    currentGroup.groups.forEach((group, currentIndex) => {
      if (group.id === projectGroup.id) {
        currentGroup.groups[currentIndex] = projectGroup;
      }
    });

    if (model.searchMode) {
      const searchGroups: ProjectGroupVO = model.rootSearchProjectGroup;
      searchGroups.groups.forEach((group, currentIndex) => {
        if (group.id === projectGroup.id) {
          searchGroups.groups[currentIndex] = projectGroup;
        }
      });
    }
    this.updatePinnedAndRecentlyUsedParentGroupMaps(model, projectGroup);
    model.currentProjectFolder = currentGroup;
  }

  private updatePinnedAndRecentlyUsedParentGroupMaps(model: ProjectNavigationModel, projectGroup: ProjectGroupVO): void {
    const parentGroupMaps: Dictionary<number, ProjectGroupVO>[] = [model.pinnedGroupsParentGroupMap, model.recentlyUsedGroupsParentGroupMap];
    parentGroupMaps.forEach((parentGroupMap: Dictionary<number, ProjectGroupVO>) => {
      parentGroupMap.values().forEach((group: ProjectGroupVO) => {
        if (group) {
          let isUpdated: boolean = false;
          let groupToCheck: ProjectGroupVO = group;
          do {
            if (groupToCheck.id === projectGroup.id) {
              groupToCheck.groupName = projectGroup.groupName;
              isUpdated = true;
            }
            groupToCheck = groupToCheck.parentGroup;
          } while (!isUpdated && groupToCheck && groupToCheck.parentGroup);
        }
      });
    });
  }

  public async deleteGroup(model: ProjectNavigationModel, projectGroup: ProjectGroupVO): Promise<boolean> {
    if (!projectGroup) {
      return;
    }
    const isConfigurationGroup: boolean = ProjectGroupUtils.isConfigurationGroupType(projectGroup.groupType);
    const removeTitle: string = isConfigurationGroup ? "save.title.info_remove_project" : "save.title.info_remove_group";
    const removeInfoBeforeMessage: string = isConfigurationGroup
      ? "save.message.info_before_remove_project"
      : "save.message.info_before_remove_group";

    const answer = await this.alertHelper.showAlert(removeTitle, removeInfoBeforeMessage, null, [AlertButton.YES, AlertButton.NO], "info");
    if (answer !== AlertButton.YES) {
      return false;
    }

    try {
      const result = await lastValueFrom(this.projectGroupDataService.removeGroupByIdRecursive(projectGroup.id));
      if (result) {
        this.groupDeleted(model, projectGroup);
      } else {
        const removeInfoMessage: string = isConfigurationGroup ? "save.message.info_remove_project" : "save.message.info_remove_group";
        await this.alertHelper.showAlert(removeTitle, removeInfoMessage, null, [AlertButton.OK], "info");
      }
      return result;
    } catch (error) {
      const errorRemoveTitle: string = isConfigurationGroup ? "save.title.info_remove_project" : "save.title.info_remove_group";
      const errorRemoveMessage: string = isConfigurationGroup ? "save.message.error_remove_project" : "save.message.error_remove_group";
      await this.showErrorMessage(error, errorRemoveTitle, errorRemoveMessage);
      return false;
    }
  }

  private groupDeleted(model: ProjectNavigationModel, projectGroup: ProjectGroupVO) {
    model.selectedProjectGroups.remove(projectGroup.id);

    const parentGroup = model.parentGroupMap.getValue(projectGroup.id);
    const index = parentGroup.groups.findIndex((g) => g.id === projectGroup.id);
    parentGroup.groups.splice(index, 1);

    const rootPinnedGroup: ProjectGroupVO = model.rootProjectGroup.groups.find(
      (group) => group.groupName === ProjectNavigationModel.ROOT_NAME_PINNED,
    );
    if (rootPinnedGroup) {
      const index = rootPinnedGroup.groups.findIndex((g) => g.id === projectGroup.id);
      if (index !== -1) {
        rootPinnedGroup.groups.splice(index, 1);
      }
    }

    if (model.searchMode) {
      const currentRootGroup = model.rootSearchProjectGroup;
      const index = currentRootGroup.groups.findIndex((g) => g.id === projectGroup.id);
      currentRootGroup.groups.splice(index, 1);
      model.currentProjectFolder = model.rootSearchProjectGroup;
    } else {
      this.openGroup(model, parentGroup);
    }
  }

  private async showErrorMessage(error: any, errorTitle: string, errorMessage: string): Promise<void> {
    if (error && NumberUtils.isNumber(error["status"]) && WagoApiStatusCode.Forbidden === error.status) {
      await this.alertHelper.showError("serverCommunication.title.user_has_no_rights", "serverCommunication.message.user_has_no_rights");
    } else {
      // TODO: Improve usage of error message parameter here -> introduce and use different error code and use dedicated messages from language file
      const message: string = error && error.statusText ? error.statusText : "";
      await this.alertHelper.showError(errorTitle, errorMessage, {
        translationParams: [message],
      });
    }
  }

  public toggleGroupVisibility(model: ProjectNavigationModel, projectGroup: ProjectGroupVO): Observable<boolean> {
    if (!projectGroup) {
      return of(false);
    }
    return this.projectGroupDataService.changeProjectGroupVisibility(projectGroup.id, !projectGroup.isHidden).pipe(
      tap((isSuccess: boolean) => {
        if (isSuccess) {
          const isHidden: boolean = !projectGroup.isHidden;
          this.updateRootGroupsOnGroupVisibilityChanged(model, projectGroup, isHidden);
        }
      }),
    );
  }

  private updateRootGroupsOnGroupVisibilityChanged(model: ProjectNavigationModel, projectGroup: ProjectGroupVO, isHidden: boolean): void {
    const projectGroupsInRootGroups: ProjectGroupVO[] = this.getProjectGroupsFromRootGroups(model, projectGroup.id);
    projectGroupsInRootGroups.forEach((group) => (group.isHidden = isHidden));

    const rootOwnGroup: ProjectGroupVO = model.rootProjectGroup.groups.find(
      (group) => group.groupName === ProjectNavigationModel.ROOT_NAME_MY_PROJECTS,
    );

    if (!rootOwnGroup) return;

    const parentGroup: ProjectGroupVO = projectGroup.parentGroup
      ? ProjectGroupUtils.findProjectGroupInATree(rootOwnGroup.groups, projectGroup.parentGroup.id)
      : rootOwnGroup;

    if (parentGroup && parentGroup.childContentLoaded) {
      model.parentGroupMap.setValue(projectGroup.id, rootOwnGroup);
    }
  }

  public isEditProjectGroupEnabled(projectGroup: ProjectGroupVO): boolean {
    if (!projectGroup || !this.userModel.user) {
      return false;
    }
    if (projectGroup.groupType !== 0 && !ProjectGroupUtils.isConfigurationGroupType(projectGroup.groupType)) {
      return false;
    }
    if (this.userModel.user.hasPermission("RENAME_PROJECT_GROUPS_FROM_ALL_USERS")) {
      return true;
    }
    if (this.userModel.user.isAdmin()) {
      return true;
    }
    if (projectGroup.groupOwner.userId === this.userModel.user.id) {
      return true;
    }
    if (
      ProjectGroupUtils.isConfigurationGroupType(projectGroup.groupType) &&
      (projectGroup.isSharedReadOnly === false || projectGroup.isSharedReadOnly === true)
    ) {
      return true;
    }
    if (ProjectGroupUtils.isFolderGroupType(projectGroup.groupType) && projectGroup.isSharedReadOnly === false && projectGroup.groupOwner.userId !== 0) {
      return true;
    }
    if (
      ProjectGroupUtils.isConfigurationGroupType(projectGroup.groupType) &&
      (projectGroup.isSharedReadOnly === false || projectGroup.isSharedReadOnly === true)
    ) {
      return true;
    }

    return false;
  }

  public isDeleteProjectGroupEnabled(projectGroup: ProjectGroupVO): boolean {
    if (!projectGroup || !this.userModel.user) {
      return false;
    }
    if (projectGroup.groupType !== 0 && !ProjectGroupUtils.isConfigurationGroupType(projectGroup.groupType)) {
      return false;
    }
    if (this.userModel.user.hasPermission("DELETE_PROJECT_GROUPS_FROM_ALL_USERS")) {
      return true;
    }
    if (this.userModel.user.isAdmin()) {
      return true;
    }
    if (projectGroup.groupOwner.userId === this.userModel.user.id) {
      return true;
    }
    if (projectGroup.isSharedReadOnly === false) {
      return true;
    }
    return false;
  }

  public async initCopyProjectOrConfigurationDialog(model: ProjectNavigationModel, item: ProjectGroupVO | ProjectHeadVO): Promise<void> {
    let copyProperties: CopyProjectOperationProperties;
    try {
      copyProperties = await this.projectComponentPresenter.openCopyProjectPopup(item);
    } catch (error) {
      Log.error("Could not open CopyProjectPopup");
      return;
    }
    if (copyProperties) {
      try {
        const result: MoveOrCopyOperationResult = await this.projectNavigationPresenter.openCopyProjectDialog(
          item,
          model.compositeCheckBoxValue,
          copyProperties,
          model.currentTab,
        );
        this.moveOrCopyDialogClosed(model);
        await this.copyOperationSuccess(model, item, result.item, result.targetFilterGroup);
      } catch (error) {
        Log.error("Could not open CopyProjectDialog");
      }
    }
  }

  public async copyProjectOrConfiguration(
    model: ProjectNavigationModel,
    copyProperties: CopyProjectOperationProperties,
    item: ProjectGroupVO | ProjectHeadVO,
    targetGroup: ProjectGroupVO,
    newName: string,
  ): Promise<MoveOrCopyOperationResult> {
    let projectItem: ProjectHeadVO | ProjectGroupVO;
    const targetFilterGroup: ProjectNavigationTab = model.currentTab;

    try {
      if (item instanceof ProjectHeadVO) {projectItem = await lastValueFrom(
        this.projectDataService.copyProjectOrCompositeArticleProject(
          item.id,
          targetGroup.id,
          copyProperties.copyConfigurationProperties,
          model.compositeCheckBoxValue,
          newName,
        ));
      } else if (item instanceof ProjectGroupVO) {
        const targetGroupId = targetGroup.id < 0 ? null : targetGroup.id;
        if (model.compositeCheckBoxValue) {
          projectItem = await lastValueFrom(
            this.articleProjectGroupDataService.copyArticleProjectGroup(item.id, targetGroupId, copyProperties, newName),
          );
        } else {
          projectItem = await lastValueFrom(
            this.projectGroupDataService.copyProjectGroup(item.id, targetGroupId, copyProperties, newName, item.isHidden),
          );
        }
      } else {
        return null;
      }
    } catch (error) {
      await this.showErrorMessage(error, "global.title.error", "copyProject.error.message.unexpected_error");
      return null;
    }

    if (!projectItem) {
      await this.alertHelper.showError("global.title.error", "copyProject.error.message.not_found");
    }
    const result: MoveOrCopyOperationResult = {
      item: projectItem,
      targetFilterGroup,
    };
    return result;
  }

  public async initMoveProjectOrConfigurationDialog(model: ProjectNavigationModel, item: ProjectGroupVO | ProjectHeadVO): Promise<void> {
    try {
      const result: MoveOrCopyOperationResult = await this.projectNavigationPresenter.openMoveProjectDialog(
        item,
        !!model.compositeCheckBoxValue,
        model.currentTab,
      );
      if (result) {
        this.moveOrCopyDialogClosed(model);
        await this.moveOperationSuccess(model, item, result.item, result.targetFilterGroup);
      }
    } catch (error) {
      Log.error("Could not open MoveProjectDialog: ", error);
    }
  }

  public async moveProjectOrConfiguration(
    model: ProjectNavigationModel,
    item: ProjectGroupVO | ProjectHeadVO,
    targetGroup: ProjectGroupVO,
  ): Promise<MoveOrCopyOperationResult> {
    let projectItem: ProjectHeadVO | ProjectGroupVO;
    const targetFilterGroup: ProjectNavigationTab = model.currentTab;
    try {
      if (item instanceof ProjectHeadVO) {
        projectItem = model.compositeCheckBoxValue
          ? await lastValueFrom(this.projectDataService.moveCompositeArticleProject(item.id, targetGroup.id))
          : await lastValueFrom(this.projectDataService.moveProject(item.id, targetGroup.id));
      } else if (item instanceof ProjectGroupVO) {
        const targetGroupId = targetGroup.id < 0 ? null : targetGroup.id;
        projectItem = model.compositeCheckBoxValue
          ? await lastValueFrom(this.articleProjectGroupDataService.moveArticleProjectGroup(item.id, targetGroupId))
          : await lastValueFrom(this.projectGroupDataService.moveProjectGroup(item.id, targetGroupId, item.isHidden));
      } else {
        return null;
      }
      if (!projectItem) {
        await this.alertHelper.showError("global.title.error", "moveProject.error.message.not_found");
      }
      return {
        item: projectItem,
        targetFilterGroup,
      };
    } catch (error) {
      await this.showErrorMessage(error, "global.title.error", "moveProject.error.message.unexpected_error");
      return null;
    }
  }
}

export interface MoveOrCopyOperationResult {
  item: ProjectHeadVO | ProjectGroupVO;
  targetFilterGroup: ProjectNavigationTab;
}
